import React, { useEffect, useRef, useState } from 'react';
import Konva from 'konva';
import { Button, Drawer, Slider } from 'antd';
import { RedoOutlined, UndoOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { TGDComponent, TGDDesign, TGDTool } from '../../../models/courses/graphicDesign';
import { addComponent, duplicateComponent, removeComponent, reorderComponent,
  setBackground, TGDConstraints, TGDReorder } from '../core/document';
import { createPictogramPreset, createShapePreset, createTextPreset } from '../core/presets';
import GDStage from '../canvas/GDStage';
import GDContextToolbar from './GDContextToolbar';
import GDTheme from './GDTheme';
import GDToolRail from './GDToolRail';
import ColorsPanel from './panels/ColorsPanel';
import LayersPanel from './panels/LayersPanel';
import PictogramsPanel from './panels/PictogramsPanel';
import ShapesPanel from './panels/ShapesPanel';
import TextPanel from './panels/TextPanel';
import { useGDEditor } from './useGDEditor';
import './GDEditor.css';

export interface GDEditorProps {
  initialDesign: TGDDesign; artboard: { width: number; height: number };
  constraints: TGDConstraints; onChange: (design: TGDDesign) => void;
  readOnly?: boolean; topBarExtra?: React.ReactNode; rightPanel?: React.ReactNode;
  onStageReady?: (stage: Konva.Stage | null) => void;
}

const GDEditor: React.FC<GDEditorProps> = ({ initialDesign, artboard, constraints,
  onChange, readOnly, topBarExtra, rightPanel, onStageReady }) => {
  const { t, i18n } = useTranslation();
  const editor = useGDEditor(initialDesign, constraints);
  const [tool, setTool] = useState<TGDTool | null>('shape');
  const [zoom, setZoom] = useState<number | 'fit'>('fit');
  const [mobilePanel, setMobilePanel] = useState(false);
  const [mobileRight, setMobileRight] = useState(false);
  const first = useRef(true);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    onChangeRef.current(editor.design);
    // Committed revisions only; previews deliberately do not notify the parent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor.revision]);
  const selected = editor.design.components.find(c => c.id === editor.selectedId);
  const direction = i18n.dir() as 'ltr' | 'rtl';
  const update = (patch: Partial<TGDComponent>) => {
    if (selected && !readOnly) editor.commit(selected.id, patch);
  };
  const add = (component: Omit<TGDComponent, 'id' | 'layer'>) => {
    if (!readOnly) editor.run(design => addComponent(design, component, constraints));
  };
  const remove = (id: string) => {
    if (readOnly) return;
    editor.run(design => removeComponent(design, id));
    editor.select(null);
  };
  const move = (id: string, order: TGDReorder) => {
    if (!readOnly) editor.run(design => reorderComponent(design, id, order));
  };
  const color = (value: string, background: boolean) => {
    editor.setCurrentColor(value);
    if (readOnly) return;
    if (background) editor.run(design => setBackground(design, value, constraints));
    else if (selected) editor.commit(selected.id, { fill: value });
  };
  const duplicate = () => {
    if (selected && !readOnly) editor.run(design => duplicateComponent(design, selected.id, constraints));
  };
  const panel = tool === 'shape' ? <ShapesPanel selected={selected}
    onAdd={sides => add(createShapePreset(sides, artboard, editor.currentColor))} onUpdate={update} />
    : tool === 'pictogram' ? <PictogramsPanel onAdd={icon =>
      add(createPictogramPreset(icon, artboard, editor.currentColor))} />
      : tool === 'text' ? <TextPanel selected={selected} onAdd={kind =>
        add(createTextPreset(kind, artboard, editor.currentColor, undefined, direction))}
        onUpdate={update} />
        : tool === 'palette' ? <ColorsPanel currentColor={editor.currentColor}
          constraints={constraints} onColor={color} />
          : tool === 'layers' ? <LayersPanel design={editor.design}
            selectedId={editor.selectedId} onSelect={editor.select}
            onMove={move} onRemove={remove} /> : null;
  const choose = (next: TGDTool) => {
    setTool(next);
    setMobilePanel(window.innerWidth < 1024);
  };
  const keyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (readOnly) return;
    const target = event.target as HTMLElement;
    if (target.closest('input,textarea,[contenteditable="true"]')) return;
    const key = event.key.toLowerCase();
    const command = event.ctrlKey || event.metaKey;
    if (command && key === 'z') { event.preventDefault(); event.shiftKey ? editor.redo() : editor.undo(); }
    else if (command && key === 'y') { event.preventDefault(); editor.redo(); }
    else if (command && key === 'd') { event.preventDefault(); duplicate(); }
    else if (key === 'escape') editor.select(null);
    else if (selected && (key === 'delete' || key === 'backspace')) {
      event.preventDefault(); remove(selected.id);
    } else if (selected && ['arrowleft', 'arrowright', 'arrowup', 'arrowdown'].includes(key)) {
      event.preventDefault();
      const step = event.shiftKey ? 10 : 1;
      update({ x: selected.x + (key === 'arrowleft' ? -step : key === 'arrowright' ? step : 0),
        y: selected.y + (key === 'arrowup' ? -step : key === 'arrowdown' ? step : 0) });
    }
  };
  return <GDTheme><div className={`gd-editor${readOnly ? ' gd-readonly' : ''}`}
    dir={direction} tabIndex={0} onKeyDown={keyDown}>
    <header className="gd-topbar">
      <Button icon={<UndoOutlined />} disabled={readOnly || !editor.canUndo}
        aria-label={t('graphicDesignLesson.editor.undo')} onClick={editor.undo} />
      <Button icon={<RedoOutlined />} disabled={readOnly || !editor.canRedo}
        aria-label={t('graphicDesignLesson.editor.redo')} onClick={editor.redo} />
      {topBarExtra}
      {rightPanel && <Button className="gd-right-toggle" onClick={() => setMobileRight(true)}>
        {t('graphicDesignLesson.editor.mission')}
      </Button>}
    </header>
    {!readOnly && <GDToolRail active={tool} allowedTools={constraints.allowedTools}
      onChoose={choose} />}
    {!readOnly && <aside className="gd-drawer">{panel}</aside>}
    <main className="gd-canvas-area">
      {!readOnly && <GDContextToolbar selected={selected} currentColor={editor.currentColor}
        constraints={constraints} onUpdate={update} onColor={color}
        onDuplicate={duplicate} onForward={() => selected && move(selected.id, 'forward')}
        onBackward={() => selected && move(selected.id, 'backward')}
        onDelete={() => selected && remove(selected.id)} />}
      <GDStage design={editor.design} artboard={artboard} selectedId={editor.selectedId}
        readOnly={readOnly} zoom={zoom} direction={direction} onSelect={editor.select}
        onPreview={editor.preview} onCommit={editor.commit} onStageReady={onStageReady} />
    </main>
    {rightPanel && <aside className="gd-right-panel">{rightPanel}</aside>}
    <footer className="gd-bottombar"><Button onClick={() => setZoom('fit')}>
      {t('graphicDesignLesson.editor.fit')}
    </Button><Slider min={50} max={200} step={10} className="gd-zoom-slider"
      value={zoom === 'fit' ? 100 : zoom * 100} onChange={value => setZoom(value / 100)} />
      <span>{zoom === 'fit' ? t('graphicDesignLesson.editor.fit') : `${Math.round(zoom * 100)} %`}</span>
    </footer>
    <Drawer open={mobilePanel && !!tool} onClose={() => setMobilePanel(false)}
      title={tool && t(`graphicDesignLesson.editor.tool.${tool}`)} className="gd-mobile-drawer">
      {panel}
    </Drawer>
    <Drawer open={mobileRight} onClose={() => setMobileRight(false)}
      title={t('graphicDesignLesson.editor.mission')} className="gd-mobile-drawer">
      {rightPanel}
    </Drawer>
  </div></GDTheme>;
};

export default GDEditor;
